/**
 * Centralized SEO configuration for all pages.
 * Each key is a page identifier with meta title, description, keywords, canonical, and schemas.
 */

const DOMAIN = 'https://upost.com';
const OG_IMAGE = `${DOMAIN}/mark-twain-og.jpg`;
const SITE_NAME = 'UPost';

function page(path, title, description, keywords, schemas = []) {
  return {
    title: `${title} | UPost`,
    description,
    keywords,
    canonical: `${DOMAIN}${path}`,
    og: { type: 'website', url: `${DOMAIN}${path}`, title, description, image: OG_IMAGE, siteName: SITE_NAME },
    twitter: { card: 'summary_large_image', title, description, image: OG_IMAGE },
    schemas,
  };
}

function breadcrumbs(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${DOMAIN}${item.path}`
    }))
  };
}

export const SEO = {
  home: page('/', 'AI-Powered Writing Platform for Authors — Write, Publish, Sell', 'Free AI writing software for novelists and screenwriters. Write novels, convert to screenplays, publish on Amazon KDP, and get honest book reviews through The Authors Exchange.', 'AI writing software, novel writing platform, screenplay converter, self-publishing tools, Amazon KDP, book reviews, indie author'),

  club: page('/club', 'The Authors Exchange — Free Book Review & ARC Network', 'Join a free credit-based community where authors earn honest Amazon reviews and readers get free advance copies. 50 credits free on signup.', 'book review exchange, ARC reader network, free book reviews, Amazon reviews for authors, advance reader copy, indie author reviews, self-publishing reviews', [
    breadcrumbs([{name:'Home',path:'/'},{name:'The Authors Exchange',path:'/club'}]),
    {"@context":"https://schema.org","@type":"Service","name":"The Authors Exchange","description":"Free credit-based book review and ARC launch community for indie authors","provider":{"@type":"Organization","name":"UPost"},"offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}}
  ]),

  blog: page('/blog', "Writer's Block — Writing Tips, Book Marketing & Publishing Blog", 'Expert tips on novel writing, self-publishing, book marketing, Amazon KDP, screenplay conversion, and AI writing tools. New articles weekly.', 'writing tips, book marketing blog, self-publishing advice, Amazon KDP tips, screenplay writing, AI writing, indie author blog', [
    breadcrumbs([{name:'Home',path:'/'},{name:'Blog',path:'/blog'}])
  ]),

  pricing: page('/pricing', 'Pricing Plans — Free AI Writing Tools for Authors', 'Start free with 5,000 AI words per month. Upgrade to Pro for unlimited AI writing, screenplay conversion, and advanced publishing tools. No credit card required.', 'UPost pricing, AI writing software cost, writing tool subscription, free writing platform, author tools pricing', [
    breadcrumbs([{name:'Home',path:'/'},{name:'Pricing',path:'/pricing'}])
  ]),

  affiliate: page('/affiliate', 'Partner Program — Earn 10-25% Commission Promoting UPost', 'Join the UPost Partner Program and earn recurring commissions. Bronze (10%) to Platinum (25%) tiers. Free to join with instant approval.', 'UPost affiliate program, writing software affiliate, author tools partner, recurring commissions, earn promoting writing tools', [
    breadcrumbs([{name:'Home',path:'/'},{name:'Partner Program',path:'/affiliate'}])
  ]),

  screenplay: page('/screenplay-guide', 'AI Screenplay Converter — Turn Your Novel Into a Script for Netflix', 'Convert your novel or manuscript into a professionally formatted screenplay using AI. TV pilot scripts, feature films, and Netflix-ready formatting.', 'novel to screenplay converter, AI screenplay writer, Netflix script format, turn book into movie, manuscript to script, TV pilot writing', [
    breadcrumbs([{name:'Home',path:'/'},{name:'Screenplay Converter',path:'/screenplay-guide'}])
  ]),

  amazon: page('/amazon-publishing', 'Amazon KDP Publishing Guide — Self-Publish Your Book in 2026', 'Complete guide to publishing your book on Amazon Kindle Direct Publishing. Formatting, pricing, marketing, and launch strategies for indie authors.', 'Amazon KDP publishing, self-publish on Amazon, Kindle Direct Publishing guide, book formatting, self-publishing 2026, ebook publishing', [
    breadcrumbs([{name:'Home',path:'/'},{name:'Amazon Publishing',path:'/amazon-publishing'}])
  ]),

  copyright: page('/copyright-protection', 'Copyright Protection for Authors — Register & Protect Your Work', 'Protect your manuscript, novel, or screenplay with copyright registration. WGA Script Registry, blockchain timestamps, and legal protection tools.', 'copyright protection for authors, register copyright book, WGA script registry, protect manuscript, copyright registration online', [
    breadcrumbs([{name:'Home',path:'/'},{name:'Copyright Protection',path:'/copyright-protection'}])
  ]),

  kindle: page('/kindle-research', 'Kindle Research Pro — Amazon Book Niche & Keyword Research Tool', 'Find profitable Amazon book niches, analyze competitor keywords, and discover untapped markets for your next bestseller with AI-powered research.', 'Amazon book research tool, Kindle niche finder, book keyword research, Amazon KDP research, bestseller analysis, book market research', [
    breadcrumbs([{name:'Home',path:'/'},{name:'Kindle Research',path:'/kindle-research'}])
  ]),

  ghostwriter: page('/ghostwriter', 'AI Ghostwriter Pro — Professional AI Book Writing Service', 'Get a professionally written book using advanced AI ghostwriting technology. From outline to finished manuscript. Packages from $497.', 'AI ghostwriter, book writing service, ghostwriting, professional writer, manuscript writing, AI book writer', [
    breadcrumbs([{name:'Home',path:'/'},{name:'AI Ghostwriter',path:'/ghostwriter'}])
  ]),
};
